/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { canAddToCart } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { hasEnoughInventory } from "@monsma/App/Handlers/Data/Products/ProductsSelectors";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type OwnProps = WidgetProps & HasProductContext & ReturnType<typeof mapStateToProps>; // xng-change

// xng-change: Adding more needed props
const mapStateToProps = (state: ApplicationState, props: HasProductContext) => {
    const configurationCompleted = state.pages.productDetails.configurationCompleted;
    const variantSelectionCompleted = state.pages.productDetails.variantSelectionCompleted;

    return {
        configurationCompleted,
        variantSelectionCompleted,
        productSettings: getSettingsCollection(state).productSettings,
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            configurationCompleted,
            variantSelectionCompleted,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

export interface ProductDetailsAddToListLinkStyles {
    wrapper?: InjectableCss;
    link?: ProductAddToListLinkStyles;
}

export const addToListLinkStyles: ProductDetailsAddToListLinkStyles = {
    wrapper: {
        css: css`
            margin-top: 30px;
            text-align: center;
        `,
    },
};

const styles = addToListLinkStyles;

// xng-change: Passing the new props we've added.
const ProductDetailsAddToListLink: React.FC<OwnProps> = ({
    productContext: { product },
    variantSelectionCompleted,
    productSettings,
    hasEnoughInventory,
    canAddToCart,
}) => {
    if (!product.canAddToWishlist && !variantSelectionCompleted) {
        return null;
    }

    const ableToAddToCart = productSettings.canAddToCart && hasEnoughInventory && canAddToCart; // xng-change

    // xng-change: update the template
    return (
        <StyledWrapper {...styles.wrapper} className={`${ableToAddToCart && "able-to-add-to-cart"}`}>
            <ProductAddToListLink data-test-selector="productDetails_addToList" extendedStyles={styles.link} />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps)(ProductDetailsAddToListLink)), // xng-change
    definition: {
        displayName: "Add to List Link",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
